import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData, UpdateStatus } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import usePermissions from '../../../hooks/usePermissions';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment, Chip, MenuItem, Select, FormControl } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EstimateModal from './EstimateModal';
import BillModal from '../Bill/BillModal';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import { printEstimatePDF, shareEstimateWhatsApp } from '../../../utils/estimatePdfGenerator';

const STATUS_LABEL = { 1: 'New', 2: 'InProgress', 3: 'Pending', 4: 'Completed', 5: 'Delivered', 6: 'Cancelled' };
const STATUS_COLOR = { 1: '#1976d2', 2: '#f57c00', 3: '#9c27b0', 4: '#388e3c', 5: '#00897b', 6: '#d32f2f' };
const STATUS_OPTIONS = [
  { value: 1, label: 'New' },
  { value: 2, label: 'InProgress' },
  { value: 3, label: 'Pending' },
  { value: 4, label: 'Completed' },
  { value: 5, label: 'Delivered' },
  { value: 6, label: 'Cancelled' },
];
const LOCATION_LABEL = { 1: 'Edappally', 2: 'Palarivattom', 3: 'Padivattom' };

function EstimateIndex() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions('Transaction');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedEstimate, setSelectedEstimate] = useState(null);

  const [billModalOpen, setBillModalOpen] = useState(false);
  const [billFromEstimate, setBillFromEstimate] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyData, setCompanyData] = useState({});
  const [lookups, setLookups] = useState({});
  const [statusMap, setStatusMap] = useState({}); // { [itemId]: statusId }

  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) logout();
  }, [isAuthenticated, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    fetchTableData().finally(() => setLoading(false));
    // Fetch company details for PDF header
    fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
      .then(r => { if (r?.responseCode === '1111' && r.response_data) setCompanyData(r.response_data); })
      .catch(() => {});
    // Fetch lookup lists for PDF name resolution
    const loadLookup = (ctrl) =>
      fetchData(ctrl, `/${companyId}`, token)
        .then(r => (r?.responseCode === '1111' && r.response_data) ? (Array.isArray(r.response_data) ? r.response_data : [r.response_data]) : [])
        .catch(() => []);
    Promise.all([
      loadLookup('FrameApi'),
      loadLookup('GlassApi'),
      loadLookup('MountApi'),
      loadLookup('BoardApi'),
      loadLookup('LaminationApi'),
      loadLookup('TapeApi'),
      loadLookup('StandApi'),
      loadLookup('PrintApi'),
    ]).then(([frames, glasses, mounts, boards, laminations, tapes, stands, prints]) => {
      setLookups({ frames, glasses, mounts, boards, laminations, tapes, stands, prints });
    });
  }, [companyId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTableData = async () => {
    try {
      const response = await fetchData('EstimateApi', `/EstimateList/${companyId}/${fromDate}/${toDate}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        // seed local status map from fetched data
        const map = {};
        response.response_data.forEach(item => {
          const raw = item.StatusId ?? item.Status;
          map[item.Id] = (raw && STATUS_LABEL[raw]) ? raw : 1;
        });
        setStatusMap(map);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Estimate data:', error);
      setData([]);
    }
  };

  const handleDelete = () => {
    deleteData('EstimateApi', `${deleteItemId}`, token)
      .then(() => { fetchTableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
      .catch((error) => console.error('Error deleting record:', error));
  };

  const handleActivate = (itemId) => {
    activateData('EstimateApi', `${itemId}`, token)
      .then(() => fetchTableData())
      .catch((error) => console.error('Error activating record:', error));
  };

  const openConfirmDialog = (itemId) => { setDeleteItemId(itemId); setConfirmDialogOpen(true); };
  const closeConfirmDialog = () => { setDeleteItemId(null); setConfirmDialogOpen(false); };

  const handleOpenModal = (mode, estimate = null) => { setModalMode(mode); setSelectedEstimate(estimate); setModalOpen(true); };
  const handleCloseModal = () => { setModalOpen(false); setSelectedEstimate(null); fetchTableData(); };
  const handleSaveEstimate = () => fetchTableData();

  const handleConvertToBill = ({ header, details }) => {
    // Map estimate header → bill header fields
    const billData = {
      BillNumber: '',
      BillDate: header.EstimateDate || new Date().toISOString().split('T')[0],
      ClientId: header.ClientId ?? null,
      ClientName: header.ClientName ?? '',
      ContactNumber: header.ContactNumber ?? '',
      Remarks: header.Remarks ?? '',
      FinYear: header.FinYear ?? '',
      Amount: header.Amount ?? 0,
      OtherAmount: header.OtherAmount ?? 0,
      PackingCharge: 0,
      TotalAmount: header.TotalAmount ?? 0,
      AdvanceAmount: header.AdvanceAmount ?? 0,
      PaidAmount: header.PaidAmount ?? 0,
      CardAmount: header.CardAmount ?? 0,
      UpiAmount: header.UpiAmount ?? 0,
      BalanceAmount: header.BalanceAmount ?? 0,
      Roundoff: header.Roundoff ?? 0,
      DiscountRate: header.DiscountRate ?? 0,
      PaymentType: header.PaymentType ?? 1,
      PaymentBankId: null,
      OrderId: header.Id ?? null,
      BillDetails: (details || []).map(({ Id, EstimateId, _key, ...rest }) => ({ ...rest, _key: Date.now() + Math.random() })),
    };
    setBillFromEstimate(billData);
    setModalOpen(false);
    setBillModalOpen(true);
  };

  const handleStatusUpdate = async (item) => {
    const newStatusId = statusMap[item.Id];
    if (!newStatusId) return;
    try {
      setLoading(true);
      await UpdateStatus('EstimateApi', item.Id, newStatusId, token);
      fetchTableData();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };

  const enrichDetails = (rawDetails) => (rawDetails || []).map(d => ({
    ...d,
    Frame:      { Name: (lookups.frames      || []).find(x => x.Id === d.FrameId)?.Name || '' },
    Glass:      { Name: (lookups.glasses     || []).find(x => x.Id === d.GlassId)?.Name || '' },
    Lamination: { Name: (lookups.laminations || []).find(x => x.Id === d.LaminationId)?.Name || '' },
    Mount:      { Name: (lookups.mounts      || []).find(x => x.Id === d.MountId)?.Name || '' },
    Board:      { Name: (lookups.boards      || []).find(x => x.Id === d.BoardId)?.Name || '' },
    Stand:      { Name: (lookups.stands      || []).find(x => x.Id === d.StandId)?.Name || '' },
    Tape:       { Name: (lookups.tapes       || []).find(x => x.Id === d.TapeId)?.Name || '' },
    Print:      { PrintType: (lookups.prints || []).find(x => x.Id === d.PrintId)?.PrintType || (lookups.prints || []).find(x => x.Id === d.PrintId)?.Name || '' },
  }));

  const handlePrintPDF = async (item) => {
    try {
      setLoading(true);
      const response = await fetchData('EstimateApi', `/${item.Id}`, token);
      if (response?.responseCode === '1111' && response.response_data) {
        const fullData = {
          ...response.response_data.Estimate,
          EstimateDetails: enrichDetails(response.response_data.EstimateDetails),
        };
        printEstimatePDF(fullData, companyData);
      } else {
        printEstimatePDF({ ...item, EstimateDetails: [] }, companyData);
      }
    } catch (err) {
      console.error('PDF error:', err);
      printEstimatePDF({ ...item, EstimateDetails: [] }, companyData);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = async (item) => {
    try {
      setLoading(true);
      const response = await fetchData('EstimateApi', `/${item.Id}`, token);
      if (response?.responseCode === '1111' && response.response_data) {
        const fullData = {
          ...response.response_data.Estimate,
          EstimateDetails: enrichDetails(response.response_data.EstimateDetails),
        };
        await shareEstimateWhatsApp(fullData, companyData);
      } else {
        await shareEstimateWhatsApp({ ...item, EstimateDetails: [] }, companyData);
      }
    } catch (err) {
      console.error('WhatsApp error:', err);
      await shareEstimateWhatsApp({ ...item, EstimateDetails: [] }, companyData);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item =>
    item.ClientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.ContactNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.OrderNumber)?.includes(searchTerm)
  );
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button size="small" onClick={() => handleOpenModal('view', item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(102,126,234,0.3)' }}>
          👁️
        </Button>
      </Tooltip>
      <Tooltip title="Print Work Order" arrow>
        <Button size="small" onClick={() => handlePrintPDF(item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', background: '#e65100', boxShadow: '0 1px 4px rgba(230,81,0,0.3)' }}>
          🖨️
        </Button>
      </Tooltip>
      <Tooltip title="Share on WhatsApp" arrow>
        <Button size="small" onClick={() => handleWhatsApp(item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', background: '#25D366', boxShadow: '0 1px 4px rgba(37,211,102,0.4)' }}>
          💬
        </Button>
      </Tooltip>
      {canEdit && (
        <Tooltip title="Edit Estimate" arrow>
          <Button size="small" onClick={() => handleOpenModal('edit', item)}
            style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(17,153,142,0.3)' }}>
            ✏️
          </Button>
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title={item.IsActive ? 'Delete Estimate' : 'Activate Estimate'} arrow>
          <Button size="small" onClick={() => item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)}
            style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: item.IsActive ? '0 1px 4px rgba(255,107,107,0.3)' : '0 1px 4px rgba(78,205,196,0.3)' }}>
            {item.IsActive ? '🗑️' : '✅'}
          </Button>
        </Tooltip>
      )}
    </div>
  );

  const fmt = (v) => parseFloat(v || 0).toFixed(2);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Estimates..." />}

        <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Romans' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>🖼️ Estimates</span>
            </h4>
          </div>

          {/* Filters + Search + Add — single row */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField
              label="From Date" type="date" size="small" value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: '150px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } }, '& .MuiInputLabel-root': { fontSize: '12px', '&.Mui-focused': { color: '#c9a84c' } } }}
            />
            <TextField
              label="To Date" type="date" size="small" value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: '150px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } }, '& .MuiInputLabel-root': { fontSize: '12px', '&.Mui-focused': { color: '#c9a84c' } } }}
            />
            <Tooltip title="Fetch estimates for selected date range" arrow>
              <Button variant="contained" onClick={() => { setCurrentPage(1); setLoading(true); fetchTableData().finally(() => setLoading(false)); }}
                style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(92,61,30,0.4)' }}>
                🔍 Search
              </Button>
            </Tooltip>
            <TextField
              size="small" placeholder="Search by client, contact or order no..." value={searchTerm} onChange={handleSearchChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
            />
            {canCreate && (
              <Tooltip title="Create a new Estimate" arrow>
                <Button variant="contained" onClick={() => handleOpenModal('create')}
                  style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(102,126,234,0.4)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontSize: '12px' }}>+</span> Add Estimate
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table sx={{ minWidth: 700 }} aria-label="Estimate table">
                <TableHead>
                  <TableRow style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', height: '27px' }}>
                    {['Order #', 'Date', 'Client', 'Contact', 'Location', 'Total (₹)', 'Balance (₹)', 'Status', 'Update Status', 'Actions'].map(h => (
                      <TableCell key={h} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 10px', textAlign: h.includes('₹') ? 'right' : h === 'Actions' ? 'right' : 'left' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                        No estimates found
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.map((item, index) => (
                    <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748', fontWeight: '700' }}>{companyData.BillPrefix || ''}{item.OrderNumber}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748' }}>{fmtDate(item.EstimateDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748', fontWeight: '600' }}>{item.ClientName || (item.Master?.Name) || '—'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748' }}>{item.ContactNumber || '—'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748' }}>{LOCATION_LABEL[item.Location] || '—'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748', textAlign: 'right', fontWeight: '600' }}>₹{fmt(item.TotalAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', textAlign: 'right', fontWeight: '600', color: item.BalanceAmount > 0 ? '#d32f2f' : '#388e3c' }}>₹{fmt(item.BalanceAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px' }}>
                        {(() => {
                          const raw = item.StatusId ?? item.Status;
                          const sid = (raw && STATUS_LABEL[raw]) ? raw : 1;
                          return (
                            <Chip label={STATUS_LABEL[sid]} size="small"
                              style={{ fontSize: '10px', height: '18px', backgroundColor: STATUS_COLOR[sid], color: 'white', fontWeight: '600' }} />
                          );
                        })()}
                      </TableCell>
                      <TableCell style={{ padding: '6px 10px' }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <Select
                              value={statusMap[item.Id] || 1}
                              onChange={(e) => setStatusMap(prev => ({ ...prev, [item.Id]: e.target.value }))}
                              sx={{ fontSize: '11px', height: '24px', backgroundColor: '#fdf6ec', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c9a84c' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5c3d1e' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5c3d1e' } }}
                            >
                              {STATUS_OPTIONS.map(o => (
                                <MenuItem key={o.value} value={o.value} style={{ fontSize: '11px' }}>{o.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Tooltip title="Update Status" arrow>
                            <Button size="small" onClick={() => handleStatusUpdate(item)}
                              style={{ minWidth: '60px', height: '24px', borderRadius: '6px', background: '#388e3c', color: 'white', padding: '2px 6px', border: 'none', fontSize: '10px', fontWeight: '700', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 1px 4px rgba(56,142,60,0.4)' }}>
                              Update
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell style={{ padding: '6px 10px', textAlign: 'right' }}>{renderActions(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage} onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirm */}
        <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Delete Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>Are you sure you want to delete this Estimate record?</DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={closeConfirmDialog} style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', minHeight: '26px' }}>
              ❌ Cancel
            </Button>
            <Button onClick={handleDelete} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', minHeight: '26px' }}>
              ✅ Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        <EstimateModal open={modalOpen} onClose={handleCloseModal} onSave={handleSaveEstimate} EstimateData={selectedEstimate} mode={modalMode} companyData={companyData} onConvertToBill={handleConvertToBill} />

        <BillModal
          open={billModalOpen}
          onClose={() => { setBillModalOpen(false); setBillFromEstimate(null); }}
          onSave={() => { setBillModalOpen(false); setBillFromEstimate(null); }}
          BillData={billFromEstimate}
          mode="create"
          companyData={companyData}
          initialData={billFromEstimate}
        />
      </div>
    </Fade>
  );
}

export default EstimateIndex;
